import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  History,
  MessageSquare,
  PencilLine,
  X,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useConversationPanel } from '../../hooks/useConversationPanel';
import { getErrorMessage } from '../../lib/apiClient';
import { formatBytes, formatDate } from '../../utils/format';
import { fileOpenPath, isEditableInEditor, opensInEditorByDefault } from '../editor/editorApi';
import { downloadFile, fileKeys, getContents, getFile } from './filesApi';
import ConversationSidebar from './components/ConversationSidebar';
import FilePreview from './components/FilePreview';
import VersionHistoryModal from './components/VersionHistoryModal';

/**
 * Full-screen file viewer at /files/:fileId — a viewport takeover without the
 * app chrome (the old-app "fullscreen takeover" behavior, as a real route so
 * files are deep-linkable). Close returns to the file's parent folder.
 */
export default function FileViewerPage() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  // Version history hidden for now:
  // const [versionsOpen, setVersionsOpen] = useState(false);
  const versionsOpen = false;
  const [conversationOpen, setConversationOpen] = useConversationPanel();

  const { data: file, isLoading, error } = useQuery({
    queryKey: fileKeys.file(fileId),
    queryFn: () => getFile(fileId),
    retry: false,
  });

  // Derive close from folderId (not history) so deep links and refreshes work.
  const closePath = file?.folderId ? `/folder/${file.folderId}` : '/files';
  const close = () => navigate(closePath);

  // Sibling files in the same folder (same order as the browser: name ASC),
  // for previous/next navigation without leaving the viewer.
  const { data: siblingsData } = useQuery({
    queryKey: fileKeys.contents(file?.folderId ?? null, { viewerSiblings: true }),
    queryFn: () =>
      getContents(file?.folderId, { page: 1, limit: 100, sortBy: 'name', sortOrder: 'ASC' }),
    enabled: Boolean(file),
    staleTime: 30_000,
  });
  const siblings = siblingsData?.files?.data ?? [];
  const siblingIndex = siblings.findIndex((f) => f.id === fileId);
  const prevFile = siblingIndex > 0 ? siblings[siblingIndex - 1] : null;
  const nextFile =
    siblingIndex >= 0 && siblingIndex < siblings.length - 1 ? siblings[siblingIndex + 1] : null;

  // Office siblings open straight into the editor (fileOpenPath), everything
  // else stays in the viewer; `from` lets the editor close back to the folder.
  const goToSibling = (sibling) =>
    navigate(fileOpenPath(sibling), { state: { from: closePath } });

  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack keys while the user is typing (comments sidebar, etc.).
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (e.key === 'Escape' && !versionsOpen) navigate(closePath);
      if (e.key === 'ArrowLeft' && prevFile) goToSibling(prevFile);
      if (e.key === 'ArrowRight' && nextFile) goToSibling(nextFile);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, closePath, versionsOpen, prevFile, nextFile]);

  const handleDownload = async () => {
    const toastId = toast.loading(`Downloading ${file.name}…`);
    try {
      await downloadFile(file.id, file.name);
      toast.success('Download ready', { id: toastId });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id: toastId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-slate-400" />
          <h1 className="text-lg font-semibold text-slate-800">This file isn’t available</h1>
          <p className="text-sm text-slate-500">
            {getErrorMessage(error, 'It may have been moved or deleted.')}
          </p>
          <Button onClick={() => navigate('/files')}>Back to files</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      {/* Toolbar */}
      <header className="relative flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3">
        {/* Previous/next — centered in the toolbar */}
        {siblings.length > 1 && siblingIndex >= 0 && (
          <div className="absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 sm:flex">
            <button
              onClick={() => goToSibling(prevFile)}
              disabled={!prevFile}
              title="Previous file"
              aria-label="Previous file"
              className="rounded-lg border border-slate-200 p-2 text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-max text-xs tabular-nums text-slate-400">
              {siblingIndex + 1} / {siblings.length}
            </span>
            <button
              onClick={() => goToSibling(nextFile)}
              disabled={!nextFile}
              title="Next file"
              aria-label="Next file"
              className="rounded-lg border border-slate-200 p-2 text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
        <button
          onClick={close}
          aria-label="Back to folder"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <FileText className="h-5 w-5 shrink-0 text-slate-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">{file.name}</p>
          <p className="text-xs text-slate-400">
            {formatBytes(file.sizeBytes)} · {formatDate(file.updatedAt || file.createdAt)}
          </p>
        </div>
        {/* Only for office docs the viewer can't render — files with a native
            preview (pdf/text/images) don't need the ONLYOFFICE detour. */}
        {opensInEditorByDefault(file) && (
          <button
            onClick={() => navigate(`/files/${file.id}/edit`)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-400"
          >
            <PencilLine className="h-4 w-4" />
            {isEditableInEditor(file) ? 'Edit' : 'Open'}
          </button>
        )}
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200"
        >
          <Download className="h-4 w-4" />
          Download
        </button>
        {/* Version history hidden for now:
        <button
          onClick={() => setVersionsOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200"
        >
          <History className="h-4 w-4" />
          Versions
        </button> */}
        <button
          onClick={() => setConversationOpen((o) => !o)}
          aria-label="Toggle comments and chat"
          aria-pressed={conversationOpen}
          className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-medium transition-colors ${
            conversationOpen
              ? 'bg-slate-200 text-slate-800'
              : 'text-slate-700 hover:bg-slate-200'
          }`}
        >
          <MessageSquare className="h-4 w-4" />
          {/* Named, not just a glyph — the sidebar has to be findable again
              once someone has closed it. */}
          <span className="hidden sm:inline">Comments</span>
        </button>
        <button
          onClick={close}
          aria-label="Close viewer"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* Preview + conversation sidebar */}
      <div className="flex min-h-0 flex-1">
        <main className="scrollbar-thin min-h-0 flex-1 overflow-auto">
          <FilePreview file={file} />
        </main>
        {conversationOpen && <ConversationSidebar file={file} />}
      </div>

      {/* Version history hidden for now:
      <VersionHistoryModal
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        target={{ type: 'file', item: file }}
      /> */}
    </div>
  );
}
